// @flow
import {
  useEffect,
  useState,
  createRef,
  createContext,
  useContext,
  MutableRefObject,
  ReactNode,
  RefObject,
} from 'react'
import { findDOMNode } from 'react-dom'
// @ts-ignore
import { intersection } from 'lodash/array'
import { FormikErrors, FormikTouched, useFormikContext } from 'formik'
import { usePrevious } from 'hooks/usePrevious'

export type InputRef = (name: string) => MutableRefObject<any> | void

const ScrollToErrorContext = createContext<{ inputRef: InputRef }>({
  inputRef: () => {},
})

type Props = {
  children: ReactNode
}

/***
 * Flatten nested object into an array of single keys with
 * dot notation. e.g {
 *   parent: {
 *     child: value
 *   }
 * }
 * becomes ['parent.child']
 *
 * @param obj
 * @param prefix
 */
const keyify = (obj: { [key: string]: any }, prefix = ''): string[] =>
  Object.keys(obj).reduce((res: string[], key: string) => {
    if (Array.isArray(obj[key])) {
      return res
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      return [...res, ...keyify(obj[key], `${prefix}${key}.`)]
    }
    return [...res, prefix + key]
  }, [])

/**
 * All errors that have also been touched
 *
 * @param errors
 * @param touched
 */
const allErrorsAndTouched = (errors: FormikErrors<unknown>, touched: FormikTouched<unknown>) =>
  intersection(keyify(errors), keyify(touched))

/**
 * Context with which to handle scrolling to any new errors on the Formik state
 * once the form has been submitted or resubmitted after validation
 *
 * @param children
 * @constructor
 */
const ScrollToError = ({ children }: Props) => {
  const [submittedCount, setSubmitCount] = useState(0)
  const { isValid, isValidating, errors, touched, submitCount, isSubmitting } = useFormikContext()
  const prevFormik = usePrevious({ isValidating, isSubmitting })
  const inputs: { [name: string]: RefObject<HTMLInputElement> } = {}

  const inputRef = (input: string) => {
    inputs[input] = createRef()
    return inputs[input]
  }

  useEffect(() => {
    const { isValidating: prevIsValidating, isSubmitting: prevIsSubmitting } = prevFormik || {}
    const validatingChange = prevIsValidating !== isValidating
    const submittingChange = prevIsSubmitting !== isSubmitting
    if (
      (validatingChange || submittingChange) &&
      (submittedCount !== submitCount || submittingChange) &&
      !isValid &&
      !isValidating
    ) {
      setSubmitCount(submitCount)
      const touchedAndError = allErrorsAndTouched(errors, touched)
      const firstError = inputs[touchedAndError[0]]
      if (firstError === undefined) return

      const element = findDOMNode(inputs[touchedAndError[0]].current)
      if (element instanceof Element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [isValidating, isSubmitting, submitCount]) // eslint-disable-line react-hooks/exhaustive-deps

  return <ScrollToErrorContext.Provider value={{ inputRef }}>{children}</ScrollToErrorContext.Provider>
}

/**
 * Hook into the context to store a ref to be able to scroll into view
 * in the vent there is an error associated with the referenced element
 *
 * @param name
 */
export const useScrollToError = (name: string) => {
  const { inputRef: inputRefFn } = useContext(ScrollToErrorContext)
  return { inputRef: inputRefFn(name) }
}

export default ScrollToError
